import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Crown, TrendingUp, Download, Search, 
  Calendar, BookOpen, Mic, Shield, RefreshCw, ChevronLeft, ChevronRight,
  Trash2, AlertTriangle, BarChart3, Eye, CheckCircle, Percent, UserCog
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'affiliates'
  const [affiliates, setAffiliates] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [affiliatesLoading, setAffiliatesLoading] = useState(false);
  const [planModal, setPlanModal] = useState(null);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const limit = 20;

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchUsers();
    }
  }, [isAdmin, page, search]);

  useEffect(() => {
    if (isAdmin && activeTab === 'affiliates' && !affiliateStats) {
      fetchAffiliates();
      fetchAffiliateStats();
    }
  }, [isAdmin, activeTab]);

  const fetchAffiliates = async () => {
    setAffiliatesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/affiliates`);
      setAffiliates(response.data.affiliates || []);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      toast.error('Error al cargar afiliados');
    } finally {
      setAffiliatesLoading(false);
    }
  };

  const fetchAffiliateStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/affiliates/stats`);
      setAffiliateStats(response.data);
    } catch (error) {
      console.error('Error fetching affiliate stats:', error);
    }
  };

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      if (response.data.is_admin) {
        setIsAdmin(true);
      } else {
        toast.error('Acceso denegado: No tienes permisos de administrador');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Error verificando permisos');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/admin/users?${params}`);
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    if (activeTab === 'users') {
      await Promise.all([fetchStats(), fetchUsers()]);
    } else {
      await Promise.all([fetchAffiliates(), fetchAffiliateStats()]);
    }
    setRefreshing(false);
    toast.success('Datos actualizados');
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleDeleteUser = async (userId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario eliminado correctamente');
      setDeleteConfirm(null);
      // Refresh the user list
      await fetchUsers();
      await fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdatePlan = async (userId, isPremium, planType) => {
    setUpdatingPlan(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/plan`, {
        is_premium: isPremium,
        plan_type: planType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isPremium ? `Plan Premium (${planType}) activado` : 'Plan cambiado a Gratuito');
      setPlanModal(null);
      await fetchUsers();
      await fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar plan');
    } finally {
      setUpdatingPlan(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const totalPages = Math.ceil(totalUsers / limit);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Verificando permisos...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Volver</span>
            </button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-400" />
              <span className="font-semibold">Panel de Administración</span>
            </div>
            <Button
              onClick={handleRefresh}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white"
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'users'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Users className="w-4 h-4" />
              Usuarios
            </button>
            <button
              onClick={() => setActiveTab('affiliates')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'affiliates'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Percent className="w-4 h-4" />
              Afiliados
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Users Tab Content */}
        {activeTab === 'users' && (
          <>
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_users}</p>
                  <p className="text-xs text-slate-500">Usuarios Totales</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.premium_users}</p>
                  <p className="text-xs text-slate-500">Premium</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.users_this_week}</p>
                  <p className="text-xs text-slate-500">Esta Semana</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_exercises_completed}</p>
                  <p className="text-xs text-slate-500">Ejercicios</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Secondary Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Registros Recientes</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Hoy</span>
                  <span className="font-medium">{stats.users_today}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Esta semana</span>
                  <span className="font-medium">{stats.users_this_week}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Este mes</span>
                  <span className="font-medium">{stats.users_this_month}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Distribución de Planes</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Gratuito</span>
                  <span className="font-medium">{stats.free_users}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Premium</span>
                  <span className="font-medium text-amber-600">{stats.premium_users}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 mt-2">
                  <div 
                    className="bg-amber-500 h-2 rounded-full"
                    style={{ width: `${stats.total_users > 0 ? (stats.premium_users / stats.total_users * 100) : 0}%` }}
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-slate-200 p-4 rounded-sm">
              <h3 className="text-sm font-medium text-slate-700 mb-3">Usuarios por Nivel</h3>
              <div className="space-y-2 text-sm">
                {stats.users_by_level && Object.entries(stats.users_by_level).map(([level, count]) => (
                  <div key={level} className="flex justify-between">
                    <span className="text-slate-500">{level}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-4 border-b border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="font-semibold text-slate-900">Lista de Usuarios</h2>
              <div className="flex items-center gap-3">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar por email o nombre..."
                      className="pl-9 w-64"
                    />
                  </div>
                  <Button type="submit" variant="outline" size="sm">
                    Buscar
                  </Button>
                </form>
                <Button onClick={handleExport} variant="outline" size="sm" className="gap-2">
                  <Download className="w-4 h-4" />
                  Exportar CSV
                </Button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
                    Usuario
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
                    Nivel
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
                    Plan
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
                    Ejercicios
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
                    Registro
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{u.name || '-'}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {u.level ? (
                        <span className={`level-badge level-${u.level.toLowerCase()}`}>
                          {u.level}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_premium ? (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-sm font-medium">
                          <Crown className="w-3 h-3" />
                          Premium
                        </span>
                      ) : (
                        <span className="text-slate-500 text-sm">Gratuito</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {u.exercises_completed}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPlanModal(u)}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          title="Cambiar Plan"
                        >
                          <UserCog className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(u)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="p-4 border-t border-slate-200 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalUsers)} de {totalUsers} usuarios
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-600">
                Página {page + 1} de {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
          </>
        )}

        {/* Affiliates Tab Content */}
        {activeTab === 'affiliates' && (
          <>
            {affiliatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-pulse text-slate-600">Cargando afiliados...</div>
              </div>
            ) : (
              <>
                {/* Affiliate Stats Cards */}
                {affiliateStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white border border-slate-200 p-4 rounded-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{affiliateStats.total_affiliates}</p>
                          <p className="text-xs text-slate-500">Afiliados Totales</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 p-4 rounded-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{affiliateStats.total_referrals}</p>
                          <p className="text-xs text-slate-500">Referencias Totales</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 p-4 rounded-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">{affiliateStats.converted_referrals}</p>
                          <p className="text-xs text-slate-500">Convertidos</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 p-4 rounded-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <Crown className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900">€{(affiliateStats.total_commissions || 0).toFixed(2)}</p>
                          <p className="text-xs text-slate-500">Comisiones Totales</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Affiliates List */}
                <div className="bg-white border border-slate-200 rounded-sm">
                  <div className="p-4 border-b border-slate-200">
                    <h2 className="font-semibold text-slate-900">Lista de Afiliados</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Afiliado</th>
                          <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Código</th>
                          <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Referencias</th>
                          <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Convertidos</th>
                          <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Balance</th>
                          <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Total Ganado</th>
                          <th className="text-center text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Estado</th>
                          <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Registro</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {affiliates.map((affiliate) => (
                          <tr key={affiliate.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{affiliate.user_name || 'Sin nombre'}</p>
                                <p className="text-xs text-slate-500">{affiliate.user_email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <code className="bg-slate-100 px-2 py-1 rounded text-sm font-mono text-slate-700">
                                {affiliate.affiliate_code}
                              </code>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm font-medium text-slate-900">{affiliate.total_referrals || 0}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm font-medium text-green-600">{affiliate.converted_referrals || 0}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-medium text-slate-900">€{(affiliate.balance || 0).toFixed(2)}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-medium text-amber-600">€{(affiliate.total_earned || 0).toFixed(2)}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                affiliate.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-slate-100 text-slate-600'
                              }`}>
                                {affiliate.status === 'active' ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {formatDate(affiliate.created_at)}
                            </td>
                          </tr>
                        ))}
                        {affiliates.length === 0 && (
                          <tr>
                            <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                              Aún no hay afiliados registrados
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Eliminar Usuario</h3>
            </div>
            <p className="text-slate-600 mb-2">
              ¿Estás seguro de que quieres eliminar este usuario?
            </p>
            <div className="bg-slate-50 p-3 rounded-lg mb-4">
              <p className="font-medium text-slate-900">{deleteConfirm.name || 'Sin nombre'}</p>
              <p className="text-sm text-slate-500">{deleteConfirm.email}</p>
            </div>
            <p className="text-sm text-red-600 mb-4">
              Esta acción eliminará todos los datos del usuario y no se puede deshacer.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleDeleteUser(deleteConfirm.id)}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {planModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <UserCog className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Cambiar Plan</h3>
            </div>
            
            <div className="bg-slate-50 p-3 rounded-lg mb-4">
              <p className="font-medium text-slate-900">{planModal.name || 'Sin nombre'}</p>
              <p className="text-sm text-slate-500">{planModal.email}</p>
              <p className="text-sm mt-1">
                Plan actual: {' '}
                {planModal.is_premium ? (
                  <span className="text-amber-600 font-medium">Premium</span>
                ) : (
                  <span className="text-slate-600">Gratuito</span>
                )}
              </p>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Selecciona el nuevo plan para este usuario:
            </p>

            <div className="space-y-2 mb-6">
              {/* Founder Member - Premium Lifetime */}
              <button
                onClick={() => handleUpdatePlan(planModal.id, true, 'founder')}
                disabled={updatingPlan}
                className="w-full p-3 border-2 border-amber-200 bg-amber-50 rounded-lg text-left hover:border-amber-400 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">Founder Member</span>
                  <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                    Lifetime
                  </span>
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  Acceso Premium de por vida para beta testers
                </p>
              </button>

              {/* Premium Monthly */}
              <button
                onClick={() => handleUpdatePlan(planModal.id, true, 'monthly')}
                disabled={updatingPlan}
                className="w-full p-3 border border-slate-200 rounded-lg text-left hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-slate-800">Premium Mensual</span>
                  <span className="ml-auto text-xs text-slate-500">30 días</span>
                </div>
              </button>

              {/* Premium Yearly */}
              <button
                onClick={() => handleUpdatePlan(planModal.id, true, 'yearly')}
                disabled={updatingPlan}
                className="w-full p-3 border border-slate-200 rounded-lg text-left hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-slate-800">Premium Anual</span>
                  <span className="ml-auto text-xs text-slate-500">365 días</span>
                </div>
              </button>

              {/* Free Plan */}
              <button
                onClick={() => handleUpdatePlan(planModal.id, false, 'free')}
                disabled={updatingPlan}
                className="w-full p-3 border border-slate-200 rounded-lg text-left hover:border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-500" />
                  <span className="font-medium text-slate-800">Plan Gratuito</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Acceso limitado a ejercicios gratuitos
                </p>
              </button>
            </div>

            <Button
              variant="outline"
              onClick={() => setPlanModal(null)}
              disabled={updatingPlan}
              className="w-full"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
